/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { AnimatePresence, motion } from "framer-motion";
import { StarIcon } from "lucide-react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { MobileCarousel } from "./components";
import { testimonials as defaultTestimonials } from "./data";
import RichText from "../richText/richText.component";

interface CustomerTestimonialSectionProps {
  testimonials?: {
    id: number;
    quote: string;
    description: string;
    author: string;
    rating: number;
    authorStoreName?: string;
    authorStoreType?: string;
    authorAvatar: string;
  }[];
  heading?: string;
  subHeading?: string;
  points?: Array<string>;
  /** Badge above the heading; "" hides it. */
  badgeText?: string;
  /** Hide the sitewide trust stats ("1L+ users", ...). */
  hideTrustPoints?: boolean;
}

const DEFAULT_TRUST_POINTS = [
  "1L+ users",
  "4.8 rating on Play Store",
  "Trusted by 10000+ retail businesses",
];

export default function CustomerTestimonialSection({
  testimonials = defaultTestimonials,
  heading = "Trusted by Shop Owners Who Are Growing Their Business",
  subHeading = "Retailers across India use Fydo to increase repeat customers, bring customers back, and grow their sales - without complicated setups or heavy discounts.",
  // Default parameters only cover `undefined`, but every landing page
  // currently passes an explicit `points: []` (left blank when the page was
  // authored) rather than omitting the prop - falling back to the sitewide
  // trust stats there instead of rendering nothing is very likely the
  // intended behavior, since these are sitewide constants, not per-page data.
  points: rawPoints,
  badgeText = "Testimonials",
  hideTrustPoints = false,
}: CustomerTestimonialSectionProps) {
  const points = hideTrustPoints
    ? []
    : rawPoints?.length
      ? rawPoints
      : DEFAULT_TRUST_POINTS;
  // A page can pass an empty list to show only its heading and text.
  const hasTestimonials = testimonials.length > 0;
  const pathname = usePathname();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<"next" | "prev" | null>(null);

  // Handle navigation to the previous testimonial
  // const handlePrev = () => {
  //   setDirection("prev");
  //   setCurrentIndex((prev) =>
  //     prev === 0 ? testimonials.length - 1 : prev - 1
  //   );
  // };

  // Handle navigation to the next testimonial
  const handleNext = () => {
    setDirection("next");
    setCurrentIndex((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1,
    );
  };

  useEffect(() => {
    if (testimonials.length <= 1) return undefined;
    const interval = setInterval(() => {
      handleNext();
    }, 6000); // 5000ms = 5 seconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, [currentIndex, testimonials.length]);

  // Framer Motion variants for the back-to-front animation
  const variants = {
    initial: (direction: "next" | "prev" | null) => ({
      x: direction === "next" ? 50 : direction === "prev" ? -50 : 0,
      scale: 0.9,
      opacity: 0,
      zIndex: 0,
    }),
    animate: {
      x: 0,
      scale: 1,
      opacity: 1,
      zIndex: 10,
      transition: { duration: 0.5, ease: "easeInOut" },
    },
    exit: (direction: "next" | "prev" | null) => ({
      x: direction === "next" ? -50 : direction === "prev" ? 50 : 0,
      scale: 0.9,
      opacity: 0,
      zIndex: 0,
      transition: { duration: 0.5, ease: "easeInOut" },
    }),
  };

  const shadowCards = Array.from({ length: testimonials.length - 1 }).map(
    (_, index) => {
      const opacity = 0.2 - index * 0.05;
      const offset = (index + 1) * 2; // Adjusted for percent-based offset
      return (
        <Card
          key={index}
          className="absolute top-0 w-full max-w-3xl rounded-xl z-0 h-full "
          style={{
            transform: `translateX(${offset}%)`,
            opacity: opacity < 0 ? 0 : opacity,
            height: "100%",
          }}
        >
          <CardContent className="py-40 h-full" />
        </Card>
      );
    },
  );

  return (
    <section className="bg-gradient-to-b from-tertiary to-primary overflow-hidden py-20">
      <div className="flex scontainer px-0! xl:px-20! lg:px-10!  flex-col items-center justify-between lg:flex-row gap-6 xl:gap-25 max-w-[85rem] lg:mx-auto  ">
        <div className="flex flex-col w-full lg:w-[40%] px-6 md:px-10 lg:px-0">
          <div className="flex flex-col items-start justify-center gap-4 w-auto ">
            {/* light-to-dark-gradient is bg-brand-gradient - the same
                orange gradient as this section's own bg-primary-toned
                background, so the badge was nearly invisible here.
                glass-white (translucent white) actually shows up against
                orange. */}
            {badgeText && (
              <Badge
                variant={"glass-white"}
                className="font-medium text-white text-sm h-8 rounded-full"
              >
                {badgeText}
              </Badge>
            )}

            <h2 className="font-semibold text-white">{heading}</h2>

            <p className=" text-white font-medium">
              <RichText text={subHeading} strongClassName="font-bold" />
            </p>

            <div className="mt-3 flex flex-wrap items-center text-xs font-medium text-white/80">
              {points?.map((p, index) => (
                <div key={index} className="flex items-center">
                  <span>{p}</span>
                  {index < points.length - 1 && (
                    <div className="h-3 w-px bg-white/30 mx-3"></div>
                  )}
                </div>
              ))}
            </div>
            {/* <div className=" items-center gap-4 hidden lg:flex lg:pt-20">
              <button
                onClick={() => {}}
                className="flex items-center justify-center p-2.5 bg-white/10 rounded-2xl active:scale-90 transition-transform"
              >
                <div className="p-2 bg-white/20 rounded-xl">
                  <ChevronLeftIcon className="h-8 w-8 text-white" />
                </div>
              </button>
              <button
                onClick={handleNext}
                className="flex items-center justify-center p-2.5 bg-white/10 rounded-2xl active:scale-90 transition-transform "
              >
                <div className="p-2 bg-white/20 rounded-xl">
                  <ChevronRightIcon className="h-8 w-8 text-white" />
                </div>
              </button>
            </div> */}
          </div>
        </div>

        {/* mobile*/}
        {hasTestimonials && <MobileCarousel testimonials={testimonials} />}

        {/* desktop*/}
        {hasTestimonials && (
          <div
            className={`relative hidden lg:block w-full md:w-[60%] h-[320px] `}
          >
            {/* Main Testimonial Card with Framer Motion Animation */}
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={variants as any}
                initial="initial"
                animate="animate"
                exit="exit"
                className="absolute w-full h-full"
              >
                <Card className="w-full p-8 rounded-xl shadow-none h-full flex flex-col justify-between">
                  <CardContent className="p-0 flex flex-col gap-2">
                    <div className="flex flex-col items-start gap-4 w-full">
                      <Image
                        width={60}
                        height={10}
                        className="w-[59.47px] h-10"
                        alt="Quote"
                        src="/images/quote.png"
                      />
                      <h3 className="font-medium text-black line-clamp-2">
                        {testimonials[currentIndex].quote}
                      </h3>
                    </div>
                    <p className="text-muted-2-foreground  line-clamp-4">
                      {testimonials[currentIndex].description}
                    </p>
                  </CardContent>
                  {testimonials[currentIndex].author && (
                    <CardFooter className="p-0 flex items-center justify-between border-t">
                      <div className="flex items-start gap-4 w-full">
                        <div className="flex flex-col w-full">
                          <h4 className="font-normal text-md tracking-[-0.22px] leading-[33px]">
                            {testimonials[currentIndex].author}
                          </h4>
                          {(testimonials[currentIndex].authorStoreName ||
                            testimonials[currentIndex].authorStoreType) && (
                            <div className="flex justify-between items-center w-full text-muted-2-foreground">
                              <p className="truncate">
                                {testimonials[currentIndex].authorStoreName}
                              </p>
                              <p className="truncate">
                                {testimonials[currentIndex].authorStoreType}
                              </p>
                            </div>
                          )}
                          {pathname === "/customers" && (
                            <div className="flex items-center gap-1.5 py-2">
                              <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                  <StarIcon
                                    key={i}
                                    className="w-4 h-4 fill-amber-500 text-transparent"
                                  />
                                ))}
                              </div>
                              <span className="text-warning-1 text-xs font-semibold">
                                {testimonials[currentIndex].rating}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardFooter>
                  )}
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Dynamic Shadow Cards */}
            {shadowCards}
          </div>
        )}
      </div>
    </section>
  );
}
